import { Obj } from 'src/hooks/useSpdSearch';
import { addFormItem, resetFormItem } from 'store/slices/calculatorFormSlice';
import { AppDispatch } from 'store/store';
import { getResponseValueByParam, isScalar } from 'utilities';
import { apiInstance } from './Api';

/**
 * SPD search configuration names for the sequential vehicle-details chain that
 * runs after the initial license-plate (VEHICLE_REGNO_LOOKUP_CODAN) lookup and
 * after the last selection in the make/model flow. All calls hit the same URL —
 * only the request body differs.
 */
export const VEHICLE_LOOKUP_SPD_NAMES = {
	VIN: 'VEHICLE_VIN_LOOKUP_CODAN',
	VARIANT_DETAILS: 'VEHICLE_VARIANT_DETAILS_CODAN',
	CARDTL: 'AGT_CARDTL',
} as const;

export type VehicleLookupResult = {
	vin: Obj;
	variantDetails: Obj;
	cardtl: Obj;
};

export type SpdParam = { param_name: string; param_value: string };

const asSpdParam = (name: string, value: unknown): SpdParam => ({
	param_name: name,
	param_value: isScalar(value) ? String(value) : '',
});

const spdSearch = async <T extends Obj = Obj>(url: string, name: string, params: SpdParam[]): Promise<T> => {
	const res = (await apiInstance().post(url, { name, params })) as { data?: { response?: unknown } };
	const response = res?.data?.response;
	if (!response || Array.isArray(response) || typeof response !== 'object') {
		throw new Error(`SPD search "${name}" at ${url} returned an unexpected payload`);
	}
	return response as T;
};

/**
 * Perform the AGT_CARDTL (car-group) lookup that terminates both search flows.
 * Reads the 8 required fields from the preceding VARIANT_DETAILS response;
 * `HIGHRISKMAKE`, `GPSREQUIRED`, `CARGROUP` are sent empty (backend fills them).
 */
export const runCarGroupLookup = async (url: string, variantDetails: Obj): Promise<Obj> => {
	if (!url) {
		throw new Error('runCarGroupLookup: missing URL');
	}
	return spdSearch(url, VEHICLE_LOOKUP_SPD_NAMES.CARDTL, [
		asSpdParam('FUEL', getResponseValueByParam(variantDetails, 'FUEL_TYPE_NAME')),
		asSpdParam('HEIGHT', getResponseValueByParam(variantDetails, 'VEH_HEIGHT')),
		asSpdParam('HK', getResponseValueByParam(variantDetails, 'VEH_HK')),
		asSpdParam('LENGTH', getResponseValueByParam(variantDetails, 'VEH_LENGHT')),
		asSpdParam('MAKE', getResponseValueByParam(variantDetails, 'MAKE_NAME')),
		asSpdParam('OWN_WEIGHT', getResponseValueByParam(variantDetails, 'VEH_OWN_WEIGHT')),
		asSpdParam('PRICE', getResponseValueByParam(variantDetails, 'NEW_VALUE_REG')),
		asSpdParam('WIDTH', getResponseValueByParam(variantDetails, 'VEH_WIDTH')),
		asSpdParam('HIGHRISKMAKE', ''),
		asSpdParam('GPSREQUIRED', ''),
		asSpdParam('CARGROUP', ''),
	]);
};

/**
 * Run the VIN → VARIANT_DETAILS → AGT_CARDTL chain that follows a successful
 * license-plate lookup. Each step feeds required fields into the next.
 *
 * Throws on network error or when a required hand-off field is missing —
 * caller is expected to surface an error modal and reset the search.
 */
export const runVehicleLookupChain = async (url: string, regnoResponse: Obj): Promise<VehicleLookupResult> => {
	if (!url) {
		throw new Error('runVehicleLookupChain: missing URL');
	}

	const serialNo = getResponseValueByParam(regnoResponse, 'serialNo');
	if (!serialNo) {
		throw new Error('runVehicleLookupChain: missing serialNo on REGNO response');
	}

	const vin = await spdSearch(url, VEHICLE_LOOKUP_SPD_NAMES.VIN, [asSpdParam('VIN', serialNo)]);

	const variantId = getResponseValueByParam(vin, 'variant');
	if (!variantId) {
		throw new Error('runVehicleLookupChain: missing variant on VIN response');
	}

	const variantDetails = await spdSearch(url, VEHICLE_LOOKUP_SPD_NAMES.VARIANT_DETAILS, [
		asSpdParam('MODELVARIANTID', variantId),
	]);

	const cardtl = await runCarGroupLookup(url, variantDetails);

	return { vin, variantDetails, cardtl };
};

/**
 * Persist the calculation-relevant AGT_CARDTL response (`CAR_GROUP`) into
 * `calculatorFormSlice` under the id that `PC.CODAN.AGENT` calculate expects.
 * Other response keys are ignored. Throws when `CAR_GROUP` is missing or
 * non-scalar so the caller's error path prevents completion — otherwise a
 * missing car group would let the step be marked valid and calculation would
 * run with either no car group or a stale one from a previous lookup.
 */
export const persistCarGroupResult = ({
	dispatch,
	contentType,
	page,
	cardtl,
}: {
	dispatch: AppDispatch;
	contentType: string;
	page: string;
	cardtl: Obj;
}): void => {
	const key = 'PC_PCL01_PCO01_CAR_GROUP';
	const value = getResponseValueByParam(cardtl, 'CAR_GROUP');
	if (value == null || typeof value === 'object') {
		throw new Error('persistCarGroupResult: missing CAR_GROUP on AGT_CARDTL response');
	}
	dispatch(
		addFormItem({
			key,
			item: { data: { id: key, value, type: contentType }, page },
		})
	);
};

/**
 * Remove any previously persisted `CAR_GROUP` from `calculatorFormSlice`.
 * Call at the start of a new lookup so a stale value can't leak into the
 * calculate payload if the new lookup fails partway through.
 */
export const clearCarGroupResult = (dispatch: AppDispatch): void => {
	dispatch(resetFormItem({ key: 'PC_PCL01_PCO01_CAR_GROUP' }));
};
